<script setup lang="ts">
import { ArrowRightBold } from '@element-plus/icons-vue'
import benefitIcon from '@/assets/components/benefitsComponent/icon_benefit-min.png'
import benefitIconPlaceHolder from '@/assets/components/benefitsComponent/icon_benefit-small-min.png'
import { benefitDialogVisibility } from '@/components/layout/GoatBenefit/GoatBenefitDialog'
import {
  accordionModel,
  callBacks,
  canBeUpgraded,
  closeLoginDialog,
  confirmedEmail,
  forgotPasswordMode,
  getLoginGaContext,
  goatBenefits,
  logInGameKey,
  registerMode,
  RegisterModeActions,
  showHowMode,
  visibilityLogInDialog
} from '@/components/layout/LoginDialog/LoginDialog'

const { gaLoginMethods } = gaUtils

const { buyOffer, triggerApi, isLoggedGoat } = gamesUtils
const { isMobileDevice } = tools
const gameSettings = computed(() => {
  return gamesUtils.getGameSettings(buyOffer.value?.gameKey || gamesUtils.routeGame.value?.gameKey || logInGameKey.value)
})

const route = useRoute()
const { t } = useI18n()
const form = ref<{ accountID: string | number, accountToken: string }>({
  accountID: '',
  accountToken: ''
})
const buyMode = ref(true)
const isOpenFromDeepLink = ref(false)
const isConnectMode = ref(false)

function beforeClose(done: () => void) {
  if (showHowMode.value) {
    showHowMode.value = false
  } else {
    done()
  }
}

function onCloseLoginDialog() {
  form.value.accountID = ''
  form.value.accountToken = ''
  buyMode.value = false
  isOpenFromDeepLink.value = false
  accordionModel.value = ''
  forgotPasswordMode.value = ''
  registerMode.value = ''
  confirmedEmail.value = ''
  showHowMode.value = false
  closeLoginDialog()
}
const deepLinkUrl = computed(() => {
  if (gameSettings.value?.idApp) {
    return gamesUtils.buildDeepLink(
      gameSettings.value.idApp,
      String(route?.path),
      gameSettings.value.gameKey,
      buyOffer.value?.id ?? ''
    )
  }
  return ''
})

watch(
  () => visibilityLogInDialog.value,
  () => {
    buyMode.value = !!buyOffer.value?.id
    if (buyOffer.value?.deepLink) {
      buyOffer.value.deepLink = false
      isOpenFromDeepLink.value = true
    }
  }
)
function showForm() {
  buyMode.value = false
}

const logBanMinutes = ref(0)

function onOpenLoginDialog() {
  checkBan()
}
function checkBan() {
  const banUntil = useGameStore().logBan(gameSettings.value?.gameKey)
  if (!banUntil) {
    logBanMinutes.value = 0
  }
  const diff = new Date(Number(banUntil) * 1000).getTime() - Date.now()
  if (diff > 0) {
    logBanMinutes.value = Math.ceil(diff / 1000 / 60)
  } else {
    logBanMinutes.value = 0
  }
}

watch(
  () => useGameStore().logBan(gameSettings.value?.gameKey),
  () => {
    checkBan()
  }
)
watch(triggerApi.triggerRef, () => {
  checkBan()
  isConnectMode.value = !!getLoginGaContext()?.connectMode
})
</script>

<template>
  <el-dialog
    v-model="visibilityLogInDialog"
    align-center
    modal-class="bg-black/80"
    class="gs-login-dialog rounded-2xl w-11/12 text-center overflow-hidden"
    :class="{
      'big:max-w-2xl': !showHowMode,
      'big:max-w-3xl': showHowMode,
      'bg-gs-bg-gradient-from': showHowMode && accordionModel === gaLoginMethods.goat
    }"
    destroy-on-close
    :before-close="beforeClose"
    @close="onCloseLoginDialog()"
    @paste="callBacks?.paste()"
    @open="onOpenLoginDialog"
  >
    <template #header>
      <div class="text-center pt-4">
        <div class="text-2xl big:text-3xl font-black text-gs-main-color uppercase">
          <template v-if="!showHowMode">
            <template v-if="isLoggedGoat">
              {{ t('loginDialog.connectGameTitle') }}
            </template>
            <template v-else-if="registerMode.length">
              <template v-if="registerMode === RegisterModeActions.confirmEmail">
                {{ t('goatOffer.emailVerification') }}
              </template>
              <template v-else-if="registerMode === RegisterModeActions.acceptTerms">
                {{ t('goatOffer.yourConsents') }}
              </template>
              <template v-else>
                {{ t('loginDialog.goatAccountTitle') }}<span class="text-2xl inline-block big:-translate-y-1">*</span>
              </template>
            </template>
            <template v-else-if="forgotPasswordMode.length">
              {{ t('goatOffer.forgotPassword') }}
            </template>
            <template v-else>
              {{ t('loginDialog.logInWithTitle') }}
            </template>
          </template>
        </div>
      </div>
    </template>
    <template #default>
      <div
        class="pt-3 big:pt-5 px-2 big:px-10 pb-3 max-w-lg big:max-w-none mx-auto"
        :class="{
          'bg-gs-bg bg-linear-to-b from-gs-bg-gradient-from to-gs-bg-gradient-to text-white': showHowMode && accordionModel === gaLoginMethods.goat,
          'bg-white': !(showHowMode && accordionModel === gaLoginMethods.goat)
        }"
      >
        <div
          v-if="!(forgotPasswordMode || registerMode).length"
          class="font-extrabold text-2xl uppercase"
        >
          <template v-if="showHowMode">
            <span v-if="accordionModel === gaLoginMethods.gameToken">{{ t('loginDialog.gameAccountLabel') }}</span>
            <span v-if="accordionModel === gaLoginMethods.goat">{{ t('loginDialog.goatAccountLabel') }}</span>
            <span v-if="accordionModel === gaLoginMethods.deepLink">{{ t('loginDialog.logInViaAppTitle') }}</span>
          </template>
        </div>
        <template v-if="buyMode && ((isMobileDevice && !isOpenFromDeepLink) || !isMobileDevice)">
          <template v-if="!isLoggedGoat">
            <div class="mt-5 text-xl max-w-[380px] mx-auto">
              {{ t('loginDialog.continueShoppingText') }}
            </div>
            <div class="my-5">
              <el-button
                color="#3ba6bc"
                class="text-white rounded-lg uppercase text-xl mb-5 font-black"
                size="large"
                style="--el-button-size: 54px"
                @click="showForm"
              >
                {{ t('logInBanner.logIn') }}
              </el-button>
            </div>
          </template>
        </template>
        <template v-else-if="showHowMode">
          <template v-if="accordionModel === gaLoginMethods.gameToken">
            <div
              class="text-sm big:text-xl mb-3 mt-2 big:mt-5"
            >
              {{ t('loginDialog.gameTokenIntro') }} <el-button
                type="primary"
                link
                class="underline underline-offset-2 big:text-base"
                @click="accordionModel = gaLoginMethods.goat ;showHowMode = true"
              >
                {{ t('loginDialog.goatAccountLabel') }}
              </el-button>, {{ t('loginDialog.additionalBenefitsSuffix') }}
            </div>
            <div class="ml-5 big:ml-1 mr-1 big:mx-4 mt-5 mb-16 flex flex-wrap">
              <ul class="text-left list-decimal text-sm big:text-base font-medium big:w-1/2 mb-5">
                <li>{{ t('loginDialog.step1', { name: gameSettings?.name }) }}</li>
                <li>{{ t('loginDialog.step2') }}</li>
                <li>{{ t('loginDialog.step3') }}</li>
                <li>{{ t('loginDialog.step4') }}</li>
                <li>{{ t('loginDialog.step5') }}</li>
                <li>{{ t('loginDialog.step6') }}</li>
              </ul>
              <div
                v-if="gameSettings?.logInImg?.length"
                class="big:w-1/2"
              >
                <el-image
                  width="710"
                  height="768"
                  class="px-2 mt-2"
                  :src="gameSettings?.logInImg[0]"
                  :zoom-rate="0"
                  :max-scale="1"
                  :min-scale="1"
                  :preview-src-list="gameSettings?.logInImg"
                  fit="cover"
                />
                <div class="text-sm text-neutral-150 w-full italic tracking-wide">
                  {{ t('loginDialog.preview') }}
                </div>
              </div>
              <div class="clear-both w-full">
                <el-button
                  color="#3ba6bc"
                  class="text-white rounded-lg uppercase text-xl mt-5"
                  size="large"
                  style="--el-button-size: 54px"
                  @click="showHowMode = false"
                >
                  {{ t('loginDialog.backToForm') }}
                </el-button>
              </div>
            </div>
          </template>
          <div
            v-else-if="accordionModel === gaLoginMethods.deepLink"
            class="text-justify hyphens-auto pt-3 flex flex-col gap-2 big:text-base px-2"
          >
            <div>
              {{ t('loginDialog.deepLinkStep1') }}
            </div>
            <div>
              {{ t('loginDialog.deepLinkStep2') }}
              <span
                class="text-gs-main-color hover:opacity-90 font-normal underline underline-offset-2 cursor-pointer"
                @click="accordionModel = gaLoginMethods.gameToken ;showHowMode = true"
              >{{ t('loginDialog.gameAccountLabel') }}</span>
            </div>
            <div>{{ t('loginDialog.deepLinkStep3') }}</div>
            <div>
              {{ t('loginDialog.recommendGoatPrefix') }}
              <span
                class="text-gs-main-color hover:opacity-90 font-normal underline underline-offset-2 cursor-pointer"
                @click="accordionModel = gaLoginMethods.goat ;showHowMode = true"
              >{{ t('loginDialog.goatAccountLabel') }}</span>, {{ t('loginDialog.additionalBenefitsSuffix') }}
            </div>
            <div class="w-full flex justify-center">
              <el-button
                color="#3ba6bc"
                class="text-white rounded-lg uppercase text-xl mt-5 font-semibold"
                size="large"
                style="--el-button-size: 50px"
                @click="showHowMode = false"
              >
                {{ t('loginDialog.backToForm') }}
              </el-button>
            </div>
          </div>
          <div
            v-else-if="accordionModel === gaLoginMethods.goat"
            class="flex flex-col gap-2 pt-3 text-left big:text-base text-white"
          >
            <div
              v-for="(benefit, index) in goatBenefits"
              :key="index"
              class="flex flex-row justify-start gap-x-2 big:gap-x-3 w-full"
            >
              <div>
                <el-image
                  :src="benefitIcon"
                  lazy
                  :alt="t('goatBtn.benefitIconAlt')"
                  class="w-8 big:w-9"
                >
                  <template #placeholder>
                    <img
                      :src="benefitIconPlaceHolder"
                      :alt="t('goatBtn.benefitIconAlt')"
                      class="w-8 big:w-9"
                    >
                  </template>
                </el-image>
              </div>
              <div class="grow pt-1 big:pt-2 text-left text-base">
                <h2 class="font-semibold leading-tight">
                  {{ benefit.header }}
                </h2>
              </div>
            </div>
            <div>
              {{ t('loginDialog.goatPanelText1') }}
              <a
                href="https://goat.gamesture.com"
                target="_blank"
                class="underline underline-offset-2 big:text-base hover:opacity-90 active:opacity-95"
              >https://goat.gamesture.com</a>.
            </div>
            <div>{{ t('loginDialog.goatPanelText2') }}</div>
            <div class="w-full flex justify-center">
              <el-button
                color="#3ba6bc"
                class="text-white rounded-lg uppercase text-xl mt-5 mb-5"
                size="large"
                style="--el-button-size: 54px"
                @click="showHowMode = false"
              >
                {{ t('loginDialog.backToForm') }}
              </el-button>
            </div>
          </div>
        </template>
        <div
          v-else-if="logBanMinutes"
          class="mt-5 mb-16 text-xl"
        >
          {{ t('loginDialog.logBanMessage', { minutes: logBanMinutes }) }}
        </div>
        <template v-else>
          <div class="auth-wrapper mb-3 big:mb-5">
            <el-collapse
              v-model="accordionModel"
              accordion
              class="px-2 bg-transparent"
            >
              <el-collapse-item
                :name="gaLoginMethods.goat"
                :disabled="!!(forgotPasswordMode || registerMode).length"
                class="gs-goat bg-gs-bg-gradient-from rounded-2xl px-2 bg-linear-to-b from-gs-bg-gradient-from to-gs-bg-gradient-to"
                style="--el-color-item: white;"
                :icon="ArrowRightBold"
              >
                <template #title>
                  <div class="text-white text-left leading-none">
                    <div
                      v-if="!(forgotPasswordMode || registerMode).length"
                      class="text-xl font-semibold leading-none"
                    >
                      {{ t('loginDialog.goatAccountLabel') }}<span class="text-xl -translate-y-2">*</span>
                    </div>
                    <div
                      v-if="!forgotPasswordMode.length && registerMode !== RegisterModeActions.confirmEmail && registerMode !== RegisterModeActions.acceptTerms"
                      class="font-light big:text-[1rem]"
                    >
                      {{ t('loginDialog.goatAccountSubtitle') }} <span
                        class="underline underline-offset-4 cursor-pointer"
                        @click.stop="benefitDialogVisibility = true"
                      >{{ t('loginDialog.benefits') }}</span>.
                    </div>
                  </div>
                </template>
                <div
                  id="auth-goat-wrapper"
                  class="relative"
                >
                  <transition name="gs-fade">
                    <GsGoatLoginBox
                      v-if="accordionModel === gaLoginMethods.goat"
                      :buy-mode="buyMode"
                    />
                  </transition>
                </div>
              </el-collapse-item>
              <el-collapse-item
                v-if="!(forgotPasswordMode || registerMode).length && !canBeUpgraded"
                :name="gaLoginMethods.gameToken"
                class="rounded-2xl px-2 bg-gs-game-primary mt-4"
                :icon="ArrowRightBold"
              >
                <template #title>
                  <div class="text-left text-gray-600 leading-none">
                    <div class="text-xl leading-none font-semibold">
                      {{ t('loginDialog.gameAccountLabel') }}
                    </div>
                    <div class="font-light big:text-[1rem]">
                      {{ t('loginDialog.gameAccountSubtitle') }}
                    </div>
                  </div>
                </template>
                <transition name="gs-fade">
                  <GsGameLoginBox
                    v-if="accordionModel === gaLoginMethods.gameToken"
                    :buy-mode="buyMode"
                  />
                </transition>
              </el-collapse-item>
              <el-collapse-item
                v-if="deepLinkUrl.length && isMobileDevice && !isOpenFromDeepLink && !(forgotPasswordMode || registerMode).length && !canBeUpgraded"
                :name="gaLoginMethods.deepLink"
                :disabled="!!(forgotPasswordMode || registerMode).length"
                class="rounded-2xl px-2 bg-gs-game-primary mt-4"
                :icon="ArrowRightBold"
              >
                <template #title>
                  <div class="text-left text-gray-600 leading-none">
                    <div class="text-xl leading-none font-semibold py-1.5">
                      {{ t('loginDialog.appTitle') }}
                    </div>
                  </div>
                </template>
                <transition name="gs-fade">
                  <GsAppLoginBox
                    v-if="accordionModel === gaLoginMethods.deepLink"
                    :deep-link-url="deepLinkUrl"
                  />
                </transition>
              </el-collapse-item>
            </el-collapse>
            <div
              v-if="!(forgotPasswordMode || registerMode).length && !canBeUpgraded"
              class="mt-3"
            >
              {{ t('loginDialog.learnMoreAbout') }} <br>
              <el-button
                link
                @click="accordionModel = gaLoginMethods.goat ;showHowMode = true"
              >
                {{ t('loginDialog.goatAccountLabel') }}
              </el-button>
              |
              <el-button
                link
                class="ml-0"
                @click="accordionModel = gaLoginMethods.gameToken ;showHowMode = true"
              >
                {{ t('loginDialog.gameAccountLabel') }}
              </el-button>
              <template v-if="deepLinkUrl.length && isMobileDevice && !isOpenFromDeepLink">
                |
                <el-button
                  link
                  class="ml-0"
                  @click="accordionModel = gaLoginMethods.deepLink ;showHowMode = true"
                >
                  {{ t('loginDialog.logInWithApp') }}
                </el-button>
              </template>
            </div>
          </div>
        </template>
      </div>
    </template>
  </el-dialog>
</template>

<style lang="postcss">
.gs-login-dialog {
  --el-message-close-size: 1.5rem;
  --el-dialog-padding-primary: 0.1;

  .el-dialog__header {
      margin-right: 0 !important;
  }

  .el-collapse {
    --el-collapse-header-bg-color: transparent;
    --el-collapse-content-bg-color: transparent;
    --el-collapse-header-height: auto;

    .el-collapse-item__header {
      position: relative;
      padding: 1rem 0.4rem;
      border-style: none !important;
      max-width: 28rem !important;
      margin-inline: auto !important;

      &.is-active {
        padding-bottom: 0.5rem;
      }
    }

    .el-collapse-item__arrow {
      --color: white;

      font-size: 1.2rem;
      transform-origin: center;
      margin-right: 0;
      margin-top: 0;
    }

    .el-collapse-item__wrap {
      border-style: none !important;
    }

    .el-collapse-item__content {
      border-style: none !important;
    }

    .el-collapse-item {
      &.is-disabled {
        .el-collapse-item__header {
          padding-bottom: 0;
        }

        .el-collapse-item__arrow {
          display: none;
        }
      }

      &.gs-goat:not(.is-active) {
        background-image: none !important;
      }
    }
  }

  .el-image-viewer__actions {
    display: none !important;
  }

  .el-collapse-item.is-disabled .el-collapse-item__header {
    cursor: text !important;
  }

  .auth-wrapper .el-collapse-item__header {
    position: relative;
  }

  .auth-wrapper .el-form {
    --el-component-size-large: 44px;
    --el-border-radius-base: 6px;
    --el-border-color: #5a5a5a;

    .el-button {
      --el-button-size: 50px;
    }

    .el-form-item {
      position: relative;

      .el-form-item__label {
        padding-left: 5px;
        position: absolute;
        top: -15px;
        left: 0;
        margin:0;
        font-size: 0.6em;
        color: var(--el-color-item);
      }

      &.is-error {
        .el-input__wrapper, .el-checkbox__inner  {
          background-color: var(--el-color-danger-light-9);
          box-shadow: none;
          border: none;
        }

        .el-form-item__error,
        .el-form-item__label,
        .el-checkbox__label > * {
          color: var(--el-color-danger) !important;
        }
      }
    }

    .el-input__wrapper {
      background-color: #f7f7f7;
    }
  }
}

@media (width >= 900px) {
  .gs-login-dialog {
    .auth-wrapper .el-form {
      --el-component-size-large: 56px;

      .el-button {
        --el-button-size: 54px;
      }

      .el-form-item {
        .el-form-item__label {
          top: -15px;
          font-size: 0.8em;
        }
      }
    }
  }
}
</style>
